import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faLock, faSignInAlt } from "@fortawesome/free-solid-svg-icons";
import { sendApiRequest } from "../utils";
import { Link, useNavigate } from "react-router-dom";
import Loader from "./Loader";
import { useDispatch } from "react-redux";
import { setValue } from '../features/UserInfo/userSlice';
import logo from '../assets/images/logo_new.jpeg';
import Swal from 'sweetalert2';
import { motion } from "framer-motion";

const Login = ({ setIsRefresh }) => {
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const onSubmit = async (data) => {
        try {
            const payload = { loginId: data.username, password: data.password };
            const response = await sendApiRequest('api/auth/login', payload, "POST");
            
            if (response.data.success) {
                localStorage.setItem('auth_token', response.data.token);
                dispatch(setValue(response.data.user));
                setIsRefresh("Rescheck");

                Swal.fire({
                    title: 'Welcome Back!',
                    html: `Login successful, ${response.data.user.fullName}!`,
                    icon: 'success',
                    confirmButtonText: 'Continue',
                    confirmButtonColor: '#8F8431',
                    background: '#F9F9F6'
                });

                if (response.data.user.email === 'prayagproperties2020@gmail.com') {
                    navigate('/admin');
                } else {
                    navigate('/dashboard');
                }
            } else {
                Swal.fire({
                    title: 'Login Failed',
                    text: response.data.message || 'Invalid credentials',
                    icon: 'error',
                    confirmButtonText: 'Try Again',
                    confirmButtonColor: '#8F8431',
                    background: '#F9F9F6'
                });
            }
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: error.response?.data?.message || 'Invalid credentials.',
                icon: 'error',
                confirmButtonText: 'OK',
                confirmButtonColor: '#8F8431',
                background: '#F9F9F6'
            });
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 bg-[#EDE9E0] relative overflow-hidden">
            {/* Subtle Pattern */}
            <div className="absolute inset-0 opacity-30 pointer-events-none"
                 style={{
                     backgroundImage: `radial-gradient(circle at 25% 30%, #D4C9A8 1px, transparent 1px)`,
                     backgroundSize: '30px 30px'
                 }}
            />

            {/* Neomorphic Card */}
            <motion.div
                initial={{ y: 60, opacity: 0, scale: 0.95 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 100, damping: 18 }}
                className="relative bg-[#EDE9E0] w-full max-w-md rounded-3xl p-8 sm:p-10 
                           shadow-[12px_12px_28px_#d4c9b8,-12px_-12px_28px_#ffffff]
                           border border-[#E5E0D0]"
            >
                {/* Logo */}
                <div className="flex flex-col items-center mb-8">
                    <motion.div whileHover={{ scale: 1.05 }} className="mb-4">
                        <img
                            src={logo}
                            alt="Dharaa Pariwar Logo"
                            className="h-24 w-auto drop-shadow-sm"
                        />
                    </motion.div>
                    <h1 className="text-4xl font-bold text-[#2C3A2A] tracking-tighter">
                        Dharaa Pariwar
                    </h1>
                    <p className="text-[#5F6A5A] mt-1">Welcome back to your account</p>
                </div>

                <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                    {/* Username */}
                    <div>
                        <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">
                            Username or User ID
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center">
                                <FontAwesomeIcon icon={faUser} className="text-[#8F8431] text-xl" />
                            </div>
                            <input
                                type="text"
                                placeholder="Enter username or ID"
                                className="w-full pl-12 pr-5 py-4 rounded-2xl bg-[#EDE9E0] 
                                           shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff]
                                           focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff]
                                           outline-none text-[#2C3A2A] placeholder:text-[#9A9A8A] transition-all"
                                {...register("username", {
                                    required: "Username is required",
                                    minLength: { value: 3, message: "Minimum 3 characters" }
                                })}
                            />
                        </div>
                        {errors.username && <p className="text-red-600 text-sm mt-1.5">{errors.username.message}</p>}
                    </div>

                    {/* Password */}
                    <div>
                        <div className="flex justify-between mb-2 ml-1">
                            <label className="block text-sm font-medium text-[#3C4A3A]">Password</label>
                            {/* <Link to="/forgot-password" className="text-sm text-[#8F8431] hover:underline">
                                Forgot Password?
                            </Link> */}
                        </div>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center">
                                <FontAwesomeIcon icon={faLock} className="text-[#8F8431] text-xl" />
                            </div>
                            <input
                                type="password"
                                placeholder="Enter your password"
                                className="w-full pl-12 pr-5 py-4 rounded-2xl bg-[#EDE9E0] 
                                           shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff]
                                           focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff]
                                           outline-none text-[#2C3A2A] placeholder:text-[#9A9A8A] transition-all"
                                {...register("password", {
                                    required: "Password is required",
                                    minLength: { value: 6, message: "Minimum 6 characters" }
                                })}
                            />
                        </div>
                        {errors.password && <p className="text-red-600 text-sm mt-1.5">{errors.password.message}</p>}
                    </div>

                    {/* Submit Button */}
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.97 }}
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-4 rounded-2xl font-semibold text-lg bg-[#8F8431] text-white
                                   shadow-[6px_6px_14px_#d4c9b8,-6px_-6px_14px_#ffffff]
                                   active:shadow-[inset_4px_4px_10px_#7A7130,inset_-4px_-4px_10px_#A89A5E]
                                   transition-all flex items-center justify-center gap-3 disabled:opacity-70"
                    >
                        {isSubmitting ? <Loader /> : (
                            <>
                                <FontAwesomeIcon icon={faSignInAlt} />
                                Login to Account
                            </>
                        )}
                    </motion.button>
                </form>

                {/* Footer Links */}
                <div className="mt-10 text-center space-y-4 text-sm">
                    <p className="text-[#5F6A5A]">
                        Customer? <Link to="/login-customer" className="text-[#8F8431] font-medium hover:underline">Customer Login</Link>
                    </p>
                    <div className="flex items-center gap-3">
                        <div className="h-px flex-1 bg-[#D4C9A8]"></div>
                        <span className="text-[#9A9A8A]">or</span>
                        <div className="h-px flex-1 bg-[#D4C9A8]"></div>
                    </div>
                    <p className="text-[#5F6A5A]">
                        Don't have an account? <Link to="/register" className="text-[#8F8431] font-medium hover:underline">Create new account</Link>
                    </p>
                    <p className="text-xs text-[#A8A89A] pt-4">
                        © {new Date().getFullYear()} Dharaa Pariwar. All rights reserved.
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

export default Login;